import { useCallback } from "react";
import log from "src/utils/log";

type SelectBuildingData = {
  selectedContractManageId: string;
  selectedProductId: string;
  selectedBuildingId: string;
  selectedBuildingLabel: string;
};

type SelectedDeskData = {
  deskGroupId: string;
  deskId: string;
};

type Base64FileDownloadOptions = {
  addFileNameSuffix?: boolean;
};

// postMessage type
export type PostMessageType =
  | "initializedSetProductBuildingList"
  | "initializedSetAvailableDeskGroups"
  | "initializedSetHancomAuthorizedQueryParams"
  | "hancomLogin"
  | "navigation"
  // | "callExternalBrowser"
  | "selectBuilding"
  | "selectedDesk"
  | "selectedUnavailableDesk"
  | "refreshRequested"
  | "file"
  | "openInAppBrowser"
  | "auth"
  | "member";

/**
 * ReactNativeWebView postMessage 를 관리하는 hook
 */
export default function usePostMessage() {
  // ReactNativeWebView 에 postMessage 전달
  const postMessage = useCallback((type: PostMessageType, data?: any) => {
    log("postMessage", type, data);
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type, data }));
    }
  }, []);

  /**
   * setProductBuildingList global function 이 (window.court.webViewFunction.setProductBuildingList)
   * 초기화 (재할당) 되었음을 알리는 post message
   */
  const postMessageInitializedSetProductBuildingList = useCallback(() => {
    postMessage("initializedSetProductBuildingList");
  }, [postMessage]);

  /**
   * setAvailableDeskGroups global function 이 (window.court.webViewFunction.setAvailableDeskGroups)
   * 초기화 (재할당) 되었음을 알리는 post message
   */
  const postMessageInitializedSetAvailableDeskGroups = useCallback(() => {
    postMessage("initializedSetAvailableDeskGroups");
  }, [postMessage]);

  /**
   * setHancomAuthorizedQueryParams global function 이 (window.court.webViewFunction.setHancomAuthorizedQueryParams)
   * 초기화 (재할당) 되었음을 알리는 post message
   */
  const postMessageInitializedSetHancomAuthorizedQueryParams = useCallback(() => {
    postMessage("initializedSetHancomAuthorizedQueryParams");
  }, [postMessage]);

  /**
   * “한컴 로그인” 버튼 클릭시 RN 으로 전달하는 post message
   */
  const postMessageHancomLogin = useCallback(
    (hancomClientId: string) => {
      postMessage("hancomLogin", {
        hancomClientId,
      });
    },
    [postMessage],
  );

  /**
   * 웹뷰 뒤로가기 버튼클릭시 RN 에 전달하는 post message
   */
  const postMessageGoBackNavigation = useCallback(() => {
    if (window.ReactNativeWebView) {
      postMessage("navigation", {
        event: "goBack",
      });
    } else {
      window.close();
    }
  }, [postMessage]);

  /**
   * 웹뷰 공지사항 목록으로 이동하는 post message
   */
  const postMessageGoNoticeListScreen = useCallback(() => {
    if (window.ReactNativeWebView) {
      postMessage("navigation", {
        event: "goNoticeListScreen",
      });
    } else {
      window.close();
    }
  }, [postMessage]);

  /**
   * RN 의 프로필 편집 화면으로 이동하는 post message
   *
   * 회원탈퇴 화면(유효한 계약이 없는 경우)의 [마이 > 프로필] 링크에서 호출한다.
   * 앱은 회원탈퇴 웹뷰 위에 네이티브 프로필 편집 화면을 띄운다. (TWV012 #3)
   */
  const postMessageGoMyProfileEditScreen = useCallback(() => {
    postMessage("navigation", {
      event: "goMyProfileEditScreen",
    });
  }, [postMessage]);

  /**
   * RN의 홈으로 이동하는 post message
   */
  const postMessageGoHomeOrMy = useCallback(() => {
    if (window.ReactNativeWebView) {
      postMessage("navigation", {
        event: "goHomeOrMy",
      });
    } else {
      window.close();
    }
  }, [postMessage]);

  /**
   * RN 의 인앱브라우저를 실행하는 함수를 호출하는 post message
   */
  // const postMessageCallExternalBrowser = useCallback(
  //   (href: string) => {
  //     postMessage("callExternalBrowser", { event: href });
  //   },
  //   [postMessage],
  // );

  /**
   * 공간이용안내 웹뷰화면에서 건물 변경시 RN 에 전달하는 post message
   */
  const postMessageSelectBuilding = useCallback(
    (selectBuildingData: SelectBuildingData) => {
      postMessage("selectBuilding", selectBuildingData);
    },
    [postMessage],
  );

  /**
   * 좌석배치도 웹뷰화면에서 선택버튼으로 좌석 선택시 RN 에 전달하는 post message
   */
  const postMessageSelectedDesk = useCallback(
    (selectedDeskData: SelectedDeskData) => {
      postMessage("selectedDesk", selectedDeskData);
    },
    [postMessage],
  );

  /**
   * 예약할 수 없는 좌석을 탭했을 때 RN 에 전달하는 post message
   *
   * 웹뷰는 "이 좌석이 눌렸다" 만 알린다. 어떤 사유로 막혔는지도, 뭐라고 안내할지도
   * RN 이 정한다 — 좌석칩 · 카드와 문구를 하나로 유지하기 위해서다.
   */
  const postMessageSelectedUnavailableDesk = useCallback(
    (selectedDeskData: SelectedDeskData) => {
      postMessage("selectedUnavailableDesk", selectedDeskData);
    },
    [postMessage],
  );

  /**
   * 파일 다운로드 post message
   */
  /**
   * 새로고침 버튼을 눌렀을 때 RN 에 전달하는 post message
   *
   * 예약 가능 여부는 앱이 내려주는 값이라 웹뷰가 갱신할 수 없다.
   * 앱이 받아서 다시 조회하고 `setAvailableDeskGroups` 로 주입해 준다.
   */
  const postMessageRefreshRequested = useCallback(() => {
    postMessage("refreshRequested");
  }, [postMessage]);

  /**
   * 파일 다운로드 post message
   */
  const postMessageFileDownload = useCallback(
    (fileUrl: string, fileName: string) => {
      postMessage("file", {
        event: "download",
        url: fileUrl,
        fileName: fileName,
      });
    },
    [postMessage],
  );

  /**
   * 파일 다운로드 post message
   */
  const postMessageBase64FileDownload = useCallback(
    (base64Text: string, fileName: string, options?: Base64FileDownloadOptions) => {
      postMessage("file", {
        event: "base64Download",
        base64Text: base64Text,
        fileName: fileName,
        ...options,
      });
    },
    [postMessage],
  );

  /**
   * 신청 계약 상세에서 결제 인앱 브라우저 처리
   */
  const postWebViewPaymentMessage = useCallback(
    (url: string) => {
      postMessage("openInAppBrowser", {
        event: url,
      });
    },
    [postMessage],
  );

  /**
   * 로그아웃
   */
  const postMessageRevoke = useCallback(() => {
    postMessage("auth", {
      event: "revoke",
    });
  }, [postMessage]);

  /**
   * 회원 정보 조회
   */
  const postMessageGetMember = useCallback(() => {
    postMessage("member", {
      event: "getMember",
    });
  }, [postMessage]);

  return {
    postMessageInitializedSetProductBuildingList,
    postMessageInitializedSetAvailableDeskGroups,
    postMessageInitializedSetHancomAuthorizedQueryParams,
    postMessageHancomLogin,
    postMessageGoBackNavigation,
    postMessageSelectBuilding,
    postMessageSelectedDesk,
    postMessageSelectedUnavailableDesk,
    postMessageRefreshRequested,
    postMessageFileDownload,
    postMessageBase64FileDownload,
    postMessageGoNoticeListScreen,
    postMessageGoMyProfileEditScreen,
    postMessageGoHomeOrMy,
    postWebViewPaymentMessage,
    postMessageRevoke,
    postMessageGetMember,
  };
}
